import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import Picture from './Picture';

export default function Lightbox({ images, captions, currentIndex, onClose, onPrev, onNext, types }) {
  const handlers = React.useRef({ onPrev, onNext, onClose });
  handlers.current = { onPrev, onNext, onClose };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlers.current.onPrev();
      if (e.key === 'ArrowRight') handlers.current.onNext();
      if (e.key === 'Escape') handlers.current.onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const caption = captions ? captions[currentIndex] : null;
  const isVideo = types ? types[currentIndex] === 'video' : false;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-[90vw] lg:max-w-5xl h-auto max-h-[90vh] p-0 border-none bg-black/95 overflow-hidden">
        <VisuallyHidden.Root><DialogTitle>Photo Gallery</DialogTitle></VisuallyHidden.Root>

        <button
          data-testid="lightbox-close-btn"
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <button
          data-testid="lightbox-prev-btn"
          onClick={onPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          data-testid="lightbox-next-btn"
          onClick={onNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-center w-full h-full min-h-[50vh]">
          {isVideo ? (
            <video
              data-testid="lightbox-video"
              src={images[currentIndex]}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[85vh]"
            />
          ) : (
            <Picture
              data-testid="lightbox-image"
              src={images[currentIndex]}
              alt={caption || `Photo ${currentIndex + 1} of ${images.length}`}
              className="max-w-full max-h-[85vh] object-contain"
              loading="eager"
            />
          )}
        </div>

        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
          {caption && (
            <div className="bg-black/60 text-white text-sm px-4 py-1 rounded-full font-medium">
              {caption}
            </div>
          )}
          <div className="bg-black/60 text-white text-xs px-3 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
